<template>
  <div class="svoi-select" :class="componentClass">
    <label v-if="label" class="label" :class="labelClass" :for="id">
      <span>{{ label }}</span>
      <span v-if="required" class="required">*</span>
    </label>

    <Multiselect
      :id="id"
      v-model="computedValue"
      :class="multiselectClass"
      :close-on-select="closeOnSelect"
      :disabled="disabled"
      :internal-search="internalSearch"
      :label="itemLabel"
      :custom-label="customLabel"
      :multiple="multiple"
      :options="options"
      :placeholder="placeholder"
      :searchable="true"
      :show-labels="false"
      :show-pointer="true"
      :track-by="trackBy"
      :tabindex="tabindex"
      :preserve-search="!clearSearch"
      :clear-on-select="clearSearch"
      :loading="loading"
      @close="toggleDropdown(false)"
      @open="toggleDropdown(true)"
      @blur="onBlur"
      @remove="onRemove"
      @search-change="onSearchChange"
    >
      <template #caret="{ toggle }">
        <div class="activator" :class="activatorClass" @mousedown.prevent.stop="toggle">
          <SvoiIcon icon="arrow/arrow-down-2" />
        </div>
      </template>

      <template #option="{ option }">
        <div class="multiselect-option__wrapper">
          <p :class="['multiselect-option__item', { '-pined': option[trackBy] === pinedItem }]">
            {{ getItemLabel(option) }}
          </p>
          <SvoiIcon v-bind="getOptionIcon(option)" />
        </div>
      </template>

      <template #noResult>
        <slot name="no-result">Ничего не найдено</slot>
      </template>
      <template #noOptions>
        <slot name="no-options">Список пуст</slot>
      </template>
      <template #afterList>
        <span class="multiselect__element">
          <slot name="after-list" />
        </span>
      </template>
    </Multiselect>

    <SvoiHint v-if="error" class="hint" theme="chilli">{{ error }}</SvoiHint>
  </div>
</template>

<script lang="ts">
import type { ComponentPropsOptions, PropType, Ref } from 'vue-demi'
import { computed, defineComponent, isVue2, onMounted, ref } from 'vue-demi'
import type { OptionSelect, OptionSelectKey, OptionsSelect, SelectSize } from '@svoi-ui/interfaces/select'
import { SelectSizeEnum } from '@svoi-ui/interfaces/select'
import { Multiselect } from 'vue-multiselect'
import { SvoiIcon } from '@svoi-ui/components/icon'
import { SvoiHint } from '@svoi-ui/components/hint'
import { uuid } from '@svoi-ui/shared/utils/helpers'
import { isString, isUnknownObject } from '@svoi-ui/shared/utils/guards'

const modelProp: ComponentPropsOptions = isVue2
  ? {
      value: {
        type: [Array, Object] as PropType<OptionsSelect>,
        default: () => []
      }
    }
  : {
      modelValue: {
        type: [Array, Object] as PropType<OptionsSelect>,
        default: () => []
      }
    }

export default defineComponent({
  name: 'SvoiSelect',
  components: { Multiselect, SvoiIcon, SvoiHint },
  inheritAttrs: false,
  props: {
    ...modelProp,
    placeholder: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    options: {
      type: Array as PropType<Array<OptionSelect>>,
      default: () => []
    },
    // Label from option Object, that will be visible in the dropdown.
    itemLabel: {
      type: String,
      default: 'title'
    },
    customLabel: {
      type: Function as PropType<(option: OptionsSelect) => void>,
      default: undefined
    },
    pinedItem: {
      type: String,
      default: ''
    },
    // Key to compare objects
    trackBy: {
      type: [String, Number, Symbol] as PropType<OptionSelectKey>,
      default: 'id'
    },
    internalSearch: {
      type: Boolean,
      default: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String as PropType<string | Ref<string>>,
      default: ''
    },
    size: {
      type: String as PropType<SelectSize | string>,
      default: SelectSizeEnum.l,
      validator: (value: unknown) => {
        return isString(value) && [SelectSizeEnum.m.toString(), SelectSizeEnum.l.toString()].includes(value)
      }
    },
    tabindex: {
      type: Number,
      default: 0
    },
    clearSearch: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'blur', 'search-change', 'update:modelValue'],
  setup(props, { emit }) {
    const isOpenDropdown = ref(false)
    const id = ref('')
    onMounted(() => (id.value = uuid()))

    const toggleDropdown = (isOpen: boolean) => {
      isOpenDropdown.value = isOpen
    }

    const computedValue = computed({
      get() {
        // @ts-expect-error
        return isVue2 ? props.value : props.modelValue
      },
      set(val) {
        emit(isVue2 ? 'input' : 'update:modelValue', val)
      }
    })

    const isSelectedValue = computed(() => {
      if (Array.isArray(computedValue.value)) {
        return computedValue.value.length
      }

      return !!computedValue.value
    })

    const activatorClass = computed(() => ({
      '-active': isOpenDropdown.value
    }))

    const labelClass = computed(() => ({
      '-error': !!props.error,
      '-active': isOpenDropdown.value || isSelectedValue.value,
      '-opened-dropdown': isOpenDropdown.value
    }))

    const multiselectClass = computed(() => ({
      '-disabled': props.disabled,
      '-error': !!props.error,
      '-search-center': !props.label
    }))

    const componentClass = computed(() => ({
      [`-size-${props.size}`]: true
    }))

    const getOptionIcon = (option: OptionSelect) => {
      const isSelected = props.multiple
        ? Array.isArray(computedValue.value) && computedValue.value.includes(option)
        : isUnknownObject(computedValue.value) && computedValue.value[props.trackBy] === option[props.trackBy]

      if (isSelected) {
        return { icon: 'essential/tick', class: '-checkmark-icon' }
      }

      return { icon: option.icon }
    }

    const onBlur = () => {
      emit('blur')
    }

    const onRemove = (targetOption: OptionSelect) => {
      const updatedValue = Array.isArray(computedValue.value)
        ? computedValue.value.filter(option => option[props.trackBy] !== targetOption[props.trackBy])
        : null

      emit(isVue2 ? 'input' : 'update:modelValue', updatedValue)
    }

    const onSearchChange = (value: string) => {
      emit('search-change', value)
    }

    const getItemLabel = (option: OptionSelect) => {
      if (props.customLabel) {
        return props.customLabel(option)
      }

      return option[props.itemLabel]
    }

    return {
      id,
      labelClass,
      multiselectClass,
      activatorClass,
      componentClass,
      computedValue,
      toggleDropdown,
      getOptionIcon,
      getItemLabel,
      onRemove,
      onBlur,
      onSearchChange
    }
  }
})
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
@use '@svoi-ui/assets/scss/components/select.scss';
</style>
